import * as THREE from 'three'
import type { FeatureFace, FeatureResultMesh } from '../../../mesh/feature'
import type { ImportedModelInstancedMeshUnit } from '../mesh-unit'
import { cloneOwnedMaterial } from '../../../material'

/**
 * 单个实例 occurrence 拓扑面的交互视觉投影。
 *
 * 设计说明：
 * 1. FeatureFace 继续承担拓扑语义、引用和选择身份，本对象只承担实例化后的可见面绘制；
 * 2. 覆盖对象使用单槽 InstancedMesh，并由正式 Unit 增量同步同一 Float32 实例矩阵；
 * 3. FeatureFace geometry 位于 pick host 本地坐标，额外后缀矩阵负责转换到 source Mesh 本地坐标；
 * 4. geometry 仍由语义面持有；覆盖对象拥有交互材质副本，以释放自身 RenderObject 绑定。
 */
export class ImportedModelInstancedTopologyFaceOverlayMesh extends THREE.InstancedMesh {
  /** 供场景诊断识别实例拓扑面覆盖对象的稳定标记。 */
  public readonly isImportedModelInstancedTopologyFaceOverlay = true

  /** pick host 本地坐标到 source Mesh 本地坐标的转换矩阵。 */
  private readonly hostToSourceMatrix = new THREE.Matrix4()

  /** 当前持续提供实例槽矩阵的正式 Unit。 */
  private sourceUnit: ImportedModelInstancedMeshUnit | null = null

  /** 当前实例投影对应的 source Mesh。 */
  private sourceMesh: THREE.Mesh | null = null

  /** 覆盖对象是否已经结束生命周期。 */
  private disposed = false
  /** 当前借用的材质配方，不向其注册临时覆盖对象的渲染绑定。 */
  private sourceMaterial: THREE.Material | null = null
  /** 配方的 shader 版本变化也会使独占副本失效。 */
  private sourceMaterialVersion = -1
  /** 当前覆盖对象独占的材质，切换配方或结束时释放。 */
  private ownedMaterial: THREE.Material

  /**
   * 创建单槽实例拓扑面覆盖对象。
   *
   * @param unit 正式接管 source Mesh 的实例 Unit。
   * @param sourceMesh 当前面实际所属的 source visible Mesh。
   * @param pickHost 当前 FeatureFace 的权威拓扑宿主。
   * @param featureFace 当前交互状态对应的语义面对象。
   * @param material 当前 Hover/Selected 共享材质。
   */
  public constructor(
    unit: ImportedModelInstancedMeshUnit,
    sourceMesh: THREE.Mesh,
    pickHost: FeatureResultMesh,
    featureFace: FeatureFace,
    material: THREE.Material,
  ) {
    const ownedMaterial = cloneOwnedMaterial(material)
    super(featureFace.geometry, ownedMaterial, 1)
    this.ownedMaterial = ownedMaterial
    this.sourceMaterial = material
    this.sourceMaterialVersion = material.version
    this.name = `imported-instanced-topology-face-overlay:${featureFace.uuid}`
    this.matrixAutoUpdate = false
    this.matrix.identity()
    this.frustumCulled = false
    this.castShadow = false
    this.receiveShadow = false
    /**
     * 覆盖对象必须从第一阶段拾取和导出收集中剥离，避免视觉对象污染业务身份。
     */
    this.userData.skipSelection = true
    this.userData.skipSelectionSubtree = true
    this.userData.isImportedModelOverlay = true
    try {
      this.syncFromSource(unit, sourceMesh, pickHost, featureFace, material)
    } catch (error) {
      this.dispose()
      throw error
    }
  }

  /**
   * 同步实例 Unit、局部坐标关系、面 geometry 与当前交互材质。
   *
   * @param unit 当前正式实例 Unit。
   * @param sourceMesh 当前面实际所属的 source visible Mesh。
   * @param pickHost 当前 FeatureFace 的权威拓扑宿主。
   * @param featureFace 当前语义面对象。
   * @param material 当前 Hover/Selected 共享材质。
   */
  public syncFromSource(
    unit: ImportedModelInstancedMeshUnit,
    sourceMesh: THREE.Mesh,
    pickHost: FeatureResultMesh,
    featureFace: FeatureFace,
    material: THREE.Material,
  ): void {
    if (this.disposed) {
      throw new Error(`实例拓扑面覆盖对象已释放：${this.name}`)
    }
    if (this.sourceUnit && this.sourceMesh && (this.sourceUnit !== unit || this.sourceMesh !== sourceMesh)) {
      this.sourceUnit.detachInstanceMatrixProjection(this.sourceMesh, this)
    }

    sourceMesh.updateWorldMatrix(true, false)
    pickHost.updateWorldMatrix(true, false)
    this.hostToSourceMatrix
      .copy(sourceMesh.matrixWorld)
      .invert()
      .multiply(pickHost.matrixWorld)
    if (!unit.attachInstanceMatrixProjection(sourceMesh, this, this.hostToSourceMatrix)) {
      throw new Error(`实例拓扑面覆盖对象无法解析 source Mesh 槽位：${sourceMesh.uuid}`)
    }

    this.sourceUnit = unit
    this.sourceMesh = sourceMesh
    this.geometry = featureFace.geometry
    if (this.sourceMaterial !== material || this.sourceMaterialVersion !== material.version || !this.matchesFaceMaterialRecipe(material)) {
      // 先创建下一份，再释放旧绑定；克隆失败不能使当前覆盖对象失去可用材质。
      const replacement = cloneOwnedMaterial(material)
      const previous = this.ownedMaterial
      this.ownedMaterial = replacement
      this.material = replacement
      this.sourceMaterial = material
      this.sourceMaterialVersion = material.version
      previous.dispose()
    }
    this.renderOrder = Math.max(featureFace.renderOrder, unit.object.renderOrder, 1)
    this.layers.mask = unit.object.layers.mask
    this.visible = true
  }

  /**
   * 禁止视觉覆盖对象参与射线拾取。
   *
   * @param _raycaster 当前射线检测器。
   * @param _intersects 命中结果数组。
   */
  public override raycast(
    _raycaster: THREE.Raycaster,
    _intersects: THREE.Intersection[],
  ): void {
    // 拾取继续由正式 InstancedMesh 回映射，再由 topology resolver 物化语义 FeatureFace。
  }

  /** 幂等解除矩阵联动并释放独占实例渲染资源。 */
  public disposeOverlay(): void {
    this.dispose()
  }

  /** 统一标准与业务释放入口，回收实例缓冲及材质绑定，不影响语义面或共享模板。 */
  public override dispose(): void {
    if (this.disposed) {
      return
    }
    this.disposed = true
    if (this.sourceUnit && this.sourceMesh) {
      this.sourceUnit.detachInstanceMatrixProjection(this.sourceMesh, this)
    }
    this.sourceUnit = null
    this.sourceMesh = null
    this.sourceMaterial = null
    this.removeFromParent()
    try {
      super.dispose()
    } finally {
      this.ownedMaterial.dispose()
    }
  }

  /**
   * 检查 FeatureFace 的有限交互配方，不依赖只在 shader 改变时增长的 Material.version。
   * 颜色/透明度可以原位修改而不增加版本；必须保持与语义面一致，稳定帧则不分配。
   * @param material 当前 Hover/Selected 配方。
   * @returns 是否仍可使用独占副本。
   */
  private matchesFaceMaterialRecipe(material: THREE.Material): boolean {
    const owned = this.ownedMaterial
    return owned.opacity === material.opacity && owned.transparent === material.transparent
      && owned.depthTest === material.depthTest && owned.depthWrite === material.depthWrite && owned.side === material.side
      && owned.polygonOffset === material.polygonOffset && owned.polygonOffsetFactor === material.polygonOffsetFactor
      && owned.polygonOffsetUnits === material.polygonOffsetUnits
      && (!(material instanceof THREE.MeshBasicMaterial) || (owned instanceof THREE.MeshBasicMaterial && owned.color.equals(material.color)))
  }
}
